import { migrate } from './migrations.js';
import type { SaveDoc } from './save.js';

/**
 * Хранилище — IndexedDB в браузере игрока. Сервера нет: перенос между
 * машинами делается файлом через exportSave / importSave.
 *
 * Три слота дел плюс архив закрытых прохождений: по-пацански проигранное
 * дело не исчезает, а уезжает в архив со статистикой.
 */

const DB_NAME = 'bratva';
const STORE = 'saves';
const ARCHIVE = 'archive';
const DB_VERSION = 2;

export const SAVE_SLOTS = [1, 2, 3] as const;
export type SaveSlot = (typeof SAVE_SLOTS)[number];

/** Строка архива: чем закончилось прохождение. */
export interface ArchivedRun {
  seed: string;
  mode: 'mamkin' | 'pacanski';
  outcome: 'srok' | 'mogila' | 'rayon';
  day: number;
  level: number;
  cash: number;
  territories: number;
  finishedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      if (!db.objectStoreNames.contains(ARCHIVE)) {
        db.createObjectStore(ARCHIVE, { keyPath: 'finishedAt' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB недоступна'));
  });
}

function tx<T>(store: string, mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const request = run(db.transaction(store, mode).objectStore(store));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('Ошибка обращения к хранилищу'));
      }),
  );
}

export async function saveGame(slot: number, doc: SaveDoc): Promise<void> {
  await tx(STORE, 'readwrite', (store) => store.put(doc, `slot-${slot}`));
}

export async function loadGame(slot: number): Promise<SaveDoc | null> {
  try {
    const raw = await tx<unknown>(STORE, 'readonly', (store) => store.get(`slot-${slot}`));
    return raw ? migrate(raw) : null;
  } catch (error) {
    console.warn('Сейв не прочитался, начинаем заново:', error);
    return null;
  }
}

export async function deleteGame(slot: number): Promise<void> {
  await tx(STORE, 'readwrite', (store) => store.delete(`slot-${slot}`));
}

/** Что лежит в каждом слоте — для панели выбора дела. */
export async function slotSummaries(): Promise<Record<number, SaveDoc | null>> {
  const entries = await Promise.all(SAVE_SLOTS.map(async (slot) => [slot, await loadGame(slot)] as const));
  return Object.fromEntries(entries);
}

export async function archiveRun(record: ArchivedRun): Promise<void> {
  await tx(ARCHIVE, 'readwrite', (store) => store.put(record));
}

export async function listArchive(): Promise<ArchivedRun[]> {
  try {
    const rows = await tx<ArchivedRun[]>(ARCHIVE, 'readonly', (store) => store.getAll() as IDBRequest<ArchivedRun[]>);
    return rows.sort((a, b) => b.finishedAt - a.finishedAt);
  } catch (error) {
    console.warn('Архив не прочитался:', error);
    return [];
  }
}

/** «Выгрузить дело» — JSON-файл, который тестер присылает вместе с багом. */
export function exportSave(doc: SaveDoc): string {
  return JSON.stringify(doc, null, 2);
}

/** «Загрузить дело» — тот же файл обратно, с проверкой версии схемы. */
export function importSave(text: string): SaveDoc {
  return migrate(JSON.parse(text));
}
